// @ts-nocheck
/**
 * DialerLayout — Persistent shell for all dialer screens.
 *
 * Renders: XDialSidebar (vertical) + XDialTopBar (36px) + content column +
 *          IncomingCallToast + IncomingCallPreview + AIEmailGenerator +
 *          ProspectDetailPanel + FloatingCallBar + <Outlet/>
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { SETTINGS_SECTIONS } from '@/pages/dialer/settings/sections';
import { Loader2, Users, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/i18n/locale';
import { PortalSlot } from '@/components/common/PortalSlot';
import { useDialer } from '@/contexts/DialerContext';
import { useIncomingCallSession } from '@/hooks/useIncomingCallSession';
import { api } from '@/api/api-client';
import { listCustomers } from '@/api/endpoints/customers';
import { toast } from 'sonner';

import XDialSidebar from '@/components/dialer/XDialSidebar';
import XDialTopBar from '@/components/dialer/XDialTopBar';
import UserHeaderBar from '@/components/dialer/UserHeaderBar';
import { OrganisationSwitcher } from '@/components/common/OrganisationSwitcher';
import { useTranslation } from 'react-i18next';
import OnCallStatusBar from '@/components/dialer/OnCallStatusBar';
// LiveTranscript supprime du DialerLayout (doublon SSE) — vit uniquement
// dans CallSheet maintenant. Voir commentaire ligne ~489.
import ScriptPanel from '@/components/dialer/ScriptPanel';
// Correction #3 — le controle Power Hour a quitte la barre d'outils du dialer
// pour le menu de gauche (XDialSidebar). Il ne reste ici que le bandeau de
// temps, purement passif, visible sur toutes les vues du dialer.
import { PowerHourBanner } from '@/components/dialer/PowerHourPanel';
import ListCallStatsBar from '@/components/dialer/ListCallStatsBar';
// "Contacter Kevin" (2026-09-22): floating pill, bottom-left, on every dialer page.
import { ContactKevinTrigger } from '@/components/common/ContactKevinButton';
import { useIsMobile } from '@/hooks/use-mobile';

// ── Client switcher helper ──────────────────────────────────────────────────
async function switchClient(clientEmail, companyName) {
  await api.functions.invoke('updateCallerSession', {
    client_email: clientEmail,
    client_company: companyName,
  });
  try {
    const raw = localStorage.getItem('revcall_caller');
    if (raw) {
      const prev = JSON.parse(raw);
      localStorage.setItem('revcall_caller', JSON.stringify({ ...prev, client_email: clientEmail }));
    }
  } catch (_) {}
  window.location.reload();
}
import TourGuide from '@/components/dialer/TourGuide';
import IncomingCallToast from '@/components/dialer/IncomingCallToast';
import IncomingSmsToast from '@/components/dialer/IncomingSmsToast';
import { LiveTranscriptsProvider } from '@/components/dialer/call-panel/LiveTranscriptsProvider';
// CallSheet supprimé 2026-05-21 — modale d'appel fusionnée dans ProspectDetailPanel.
// OnCallBar supprimé : sa logique "live call" est désormais intégrée dans
// FloatingCallBar (mode polymorphe selon connectedContact).
import CelebrationOverlay from '@/components/dialer/CelebrationOverlay';
import DTMFKeypad from '@/components/dialer/DTMFKeypad';
import AIEmailGenerator from '@/components/dialer/AIEmailGenerator';
import ProspectDetailPanel from '@/components/dialer/ProspectDetailPanel';
import BookedMeetingDialog from '@/components/dialer/BookedMeetingDialog';
import FloatingCallBar from '@/components/dialer/FloatingCallBar';
import GlobalSearchDialog from '@/components/dialer/GlobalSearchDialog';
import { ShortcutsHelpDialog } from '@/components/shell/ShortcutsHelpDialog';
import { isTypingTarget } from '@/lib/keyboard';
import { countOverdueFollowups } from '@/hooks/useDialerDerivedData';
import LiveFeedPanel from '@/components/dialer/LiveFeedPanel';
import TelephonyBlocker from '@/components/dialer/TelephonyBlocker';
import { DialerBannerSlot } from '@/components/shell/BannerSlot';
import IncomingCallPreview from '@/components/dialer/IncomingCallPreview';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { CustomerFormDialog } from '@/pages/TeamManagement';
import { pathForView } from './dialerRoutes';
import { SessionUnavailable } from '@/components/common/SessionUnavailable';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useScheduledCallbacks } from '@/hooks/useScheduledCallbacks';
import { displayListsPoll } from '@/config/polling';
import { getUiFlags } from '@/lib/flags';
import CallPage from '@/pages/dialer/call/CallPage';
import { useCallPageStore } from '@/state/callPageStore';
import { useLiveCallContact } from '@/hooks/useLiveCallContact';
import { DialerRail } from '@/components/shell/DialerRail';
import { VIEW_HEADER as HEADER_BY_VIEW, crmTitleKey } from '@/layouts/dialerRoutes';
import { crmProviderShown } from '@/lib/hubspotUi';

// Map URL segments to selectedView IDs for sidebar highlighting
const ROUTE_TO_VIEW = {
  '': 'queue',
  'home': 'home',
  'contacts': 'queue',
  'followups': 'followups',
  'sms': 'sms',
  'team': 'team',
  'analytics': 'analytics',
  'ai-chat': 'tools',
  'tools': 'tools',
  'crm': 'crm',
  // 2026-09-15 -- 'objections' manquait ici ET dans la table des chemins (dialerRoutes.ts). La route
  // existait pourtant dans App.tsx et l'entree existait dans le menu, mais le
  // shell ne reconnaissait pas le segment : selectedView retombait sur 'queue',
  // si bien que l'ecran Objections portait le titre du Dialer, son sous-titre,
  // sa barre d'outils (« + Ajouter », « CSV »), et surlignait « Dialer » dans le
  // menu. Rien n'indiquait a l'utilisateur ou il se trouvait.
  // Cf. BUGS-OUVERTS.md (NAV-1).
  'objections': 'objections',
  'client-portal': 'client-portal',
  'settings': 'settings',
  'call-history': 'call-history',
  'billing': 'billing',
};


// QA-W7-6: what an admin of an organisation without any client may still
// open: Paramètres, Équipe and Facturation set up the organisation (numbers,
// members, billing) and need no client. The calling screens keep the prompt.
const ADMIN_PAGES_WITHOUT_CUSTOMER = [
  { path: '/ParallelDialer/settings', labelKey: 'settings' },
  { path: '/team', labelKey: 'team' },
  { path: '/ParallelDialer/billing', labelKey: 'billing' },
];

export function adminPageWithoutCustomer(pathname: string): boolean {
  return ADMIN_PAGES_WITHOUT_CUSTOMER.some(({ path }) => pathname === path || pathname.startsWith(`${path}/`));
}

/** The organisation pages an admin reaches before creating a client (QA-W7-6). */
function NoCustomerAdminLinks({ current }: { current?: string }) {
  const { t } = useTranslation(['dialer']);
  return (
    <nav aria-label={t('dialer:layout.noCustomers.adminPages')} className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
      {ADMIN_PAGES_WITHOUT_CUSTOMER.map(({ path, labelKey }) => (
        <Link key={path} to={path} aria-current={current && adminPageWithoutCustomer(current) && current.startsWith(path) ? 'page' : undefined}
          className="inline-flex min-h-11 items-center underline-offset-2 hover:underline" style={{ color: 'var(--vf-accent-text)' }}>
          {t(`dialer:nav.${labelKey}`)}
        </Link>
      ))}
    </nav>
  );
}

/**
 * QA-W7-6: an admin without a client on Paramètres / Équipe / Facturation:
 * the page, under a bar that says why the dialer is not there and links the
 * three pages and the settings sections (the sidebar needs a client).
 */
function NoCustomerAdminFrame() {
  const { t } = useTranslation(['dialer', 'settings']);
  const location = useLocation();
  const onSettings = location.pathname.startsWith('/ParallelDialer/settings');
  return (
    <div className="min-h-screen" style={{ background: 'var(--vf-bg)' }}>
      <header className="border-b px-4 py-3 sm:px-6" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
        <p role="note" data-testid="no-customer-admin-frame" className="text-sm" style={{ color: 'var(--vf-text-2)' }}>
          {t('dialer:layout.noCustomers.adminFrame')}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-6">
          <NoCustomerAdminLinks current={location.pathname} />
          {onSettings && (
            <nav aria-label={t('dialer:nav.settings')} className="flex flex-wrap gap-x-3 text-sm">
              {SETTINGS_SECTIONS.map((s) => (
                <Link key={s.id} to={`/ParallelDialer/settings?section=${s.id}`} className="inline-flex min-h-11 items-center underline-offset-2 hover:underline" style={{ color: 'var(--vf-text-2)' }}>
                  {t(`settings:sections.${s.id}`)}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>
      {/* /team kept its p-6 frame; the dialer pages bring their own spacing. */}
      <main className={location.pathname.startsWith('/team') ? 'p-6' : undefined}>
        <Outlet />
      </main>
    </div>
  );
}

export function NoCustomersPrompt({ isAdmin, onLogout }) {
  const { t } = useTranslation(['dialer']);
  const queryClient = useQueryClient();
  const [rechecking, setRechecking] = useState(false);
  // The first Customer is created right here: saving invalidates ['customers'],
  // which the layout's sidebar query sits under, so the dialer replaces this
  // prompt as soon as the form closes.
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  return (
    <div className="bg-[var(--vf-bg)] min-h-screen flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 max-w-md w-full p-8 space-y-6 text-center">
        <div className="w-14 h-14 bg-[var(--vf-accent-bg)] rounded-full flex items-center justify-center mx-auto">
          <Users className="w-7 h-7 text-[var(--vf-accent-text)]" />
        </div>
        {isAdmin ? (
          <>
            <h2 className="text-xl font-bold text-gray-900">{t('dialer:layout.noCustomers.adminTitle')}</h2>
            <p className="text-sm text-gray-500">
              {t('dialer:layout.noCustomers.adminHint')}
            </p>
            <div className="flex flex-col items-center gap-3">
              <Button onClick={() => setShowCustomerForm(true)}>
                {t('dialer:layout.noCustomers.createFirst')}
              </Button>
              <Dialog open={showCustomerForm} onOpenChange={setShowCustomerForm}>
                {showCustomerForm && <CustomerFormDialog onClose={() => setShowCustomerForm(false)} />}
              </Dialog>
              {/* Si l'utilisateur a accès à plusieurs organisations, lui
                  permettre de switcher d'ici sans devoir se délogger. */}
              <OrganisationSwitcher />
              {/* QA-W7-6: the organisation pages need no client. */}
              <p className="text-xs text-gray-500">{t('dialer:layout.noCustomers.adminPagesHint')}</p>
              <NoCustomerAdminLinks />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-gray-900">{t('dialer:layout.noCustomers.memberTitle')}</h2>
            <p className="text-sm text-gray-500">
              {t('dialer:layout.noCustomers.memberHintBefore')} <strong>{t('dialer:layout.noCustomers.memberHintPath')}</strong> {t('dialer:layout.noCustomers.memberHintAfter')}
            </p>
            <div className="flex flex-col items-center gap-3">
              {/* QA-W6-4: once the admin has assigned a client, the member
                  checks again from here, without signing out. */}
              <Button
                disabled={rechecking}
                aria-busy={rechecking || undefined}
                onClick={async () => {
                  setRechecking(true);
                  try { await queryClient.invalidateQueries({ queryKey: ['customers'] }); } finally { setRechecking(false); }
                }}
              >
                {t('dialer:layout.noCustomers.checkAgain')}
              </Button>
              <OrganisationSwitcher />
            </div>
          </>
        )}
        {/* Toujours offrir une porte de sortie : « Se déconnecter » mène à
            /Login (QA-W6-4). Sans ça le user reste piégé si son JWT pointe
            sur une org/membership qui n'existe plus. */}
        <div className="flex flex-col items-center gap-3">
          <Button
            variant="outline"
            onClick={async () => {
              try { await onLogout?.(); } catch { /* ignore */ }
              window.location.href = '/Login';
            }}
          >
            {t('dialer:layout.noCustomers.logout')}
          </Button>
          {/* Cet écran est exactement là où un membre bloqué a besoin de
              joindre Kevin : il n'a aucun autre bouton de support ici. */}
          <ContactKevinTrigger variant="ghost" size="sm" />
        </div>
      </div>
    </div>
  );
}

export default function DialerLayout() {
  const { t } = useTranslation(['dialer']);
  const navigate = useNavigate();
  const location = useLocation();
  const d = useDialer();
  // The card and the list read the prospect queue, refetched every 5 s: after
  // an action (pause, resume, requeue, disable/enable calls) refetch it before
  // closing, or a card reopened at once still shows the previous state.
  const queryClient = useQueryClient();
  const refreshProspects = () => queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });

  // Kevin, 2026-10-01 : la fiche recevait la COPIE du contact prise au moment
  // du clic. Apres une mise en pause, la file etait bien rafraichie mais cette
  // copie ne bougeait pas : le bouton continuait a proposer « Mettre en pause »
  // sur un contact deja en pause. On relit le contact dans la file a chaque
  // rendu, et la copie ne sert plus que de repli pour un contact qui n'y est
  // pas (ouvert depuis l'historique, par exemple).
  const contactAffiche = React.useMemo(() => {
    const choisi = d.selectedProspect;
    if (!choisi) return null;
    const file = d.filteredProspectQueue || d.prospectQueue || [];
    return file.find((p) => p.id === choisi.id) || choisi;
  }, [d.selectedProspect, d.filteredProspectQueue, d.prospectQueue]);
  const incomingSession = useIncomingCallSession();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // The sidebar is hidden below md, so the pill sits at the edge there.
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // "Renseigner un rendez-vous pris" (2026-09-23) — a meeting already
  // obtained, recorded from the left nav. Distinct from the wizard above,
  // which books a new one.
  const [showBookedMeeting, setShowBookedMeeting] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  // The Tâches menu badge and the ⌘K count read the Tâches tab's own query,
  // for the active customer (W4, B2-followups): one number everywhere. It
  // polls (30 s) where the lists poll (ENG-4: Accueil, Liste, Tâches,
  // Historique); elsewhere it is read on mount and on focus.
  const { data: scheduledCallbacks = [], isLoading: overdueLoading } = useScheduledCallbacks(
    d.callerData?.client_email, true, d.selectedCustomerId, displayListsPoll(location.pathname, false),
  );
  const overdueFollowupsCount = overdueLoading ? undefined : countOverdueFollowups(scheduledCallbacks, Date.now());
  const navStats = React.useMemo(() => ({ ...d.stats, overdueFollowupsCount }), [d.stats, overdueFollowupsCount]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showLiveFeed, setShowLiveFeed] = useState(false);
  const [showLiveTranscript, setShowLiveTranscript] = useState(true);
  const [showScriptPanel, setShowScriptPanel] = useState(false);
  // Modale preview appel entrant : ouverte quand l'agent clique le toast.
  const [showIncomingPreview, setShowIncomingPreview] = useState(false);
  // Auto-close de la preview si l'appel entrant disparait (annulation, accept,
  // reject — tous remettent incomingCallConnection a null).
  useEffect(() => {
    if (!d.incomingCallConnection && showIncomingPreview) setShowIncomingPreview(false);
  }, [d.incomingCallConnection, showIncomingPreview]);
  const [rightPanel, setRightPanel] = useState('transcript'); // 'transcript' | 'script'
  // Auto-reopen transcript panel when a new call connects
  const activeCallId = d.activeCalls?.find(c => c.call_status === 'connected_to_agent')?.id;
  useEffect(() => {
    if (activeCallId) { setShowLiveTranscript(true); setRightPanel('transcript'); }
  }, [activeCallId]);

  // ── Customer list for sidebar switcher (ADR-024) ────────────────────────
  // Replaces the legacy Tenant switcher. The sidebar now shows the
  // tenant's Customers (B2B clients) and selecting one scopes the entire
  // dialer view (prospects/calls/meetings/analytics/portal) to it.
  //
  // Visibility rules:
  //   - tenant_admin / super_admin → all active customers of the tenant
  //   - caller → only customers in their Caller.customer_ids[] assignment
  //   - super_admin's tenant switching is handled separately by the
  //     OrganisationSwitcher in UserHeaderBar.
  const isAdminLike =
    d.callerData?.role === 'admin' ||
    d.callerData?.role === 'tenant_admin' ||
    d.callerData?.role === 'super_admin' ||
    d.callerData?.is_super_admin === true;
  const { data: allSidebarCustomers = [], isPending: customersPending } = useQuery({
    // Under the 'customers' prefix on purpose: every Customer mutation in
    // TeamManagement invalidates ['customers'], and the empty-state gate below
    // must lift as soon as the first Customer is created.
    queryKey: ['customers', 'sidebar', { activeOnly: true }],
    queryFn: ({ signal }) => listCustomers({ activeOnly: true, signal }),
    enabled: !!d.callerData,
    staleTime: 60_000,
  });
  const callerCustomerIds: string[] = d.callerData?.customer_ids ?? [];
  const sidebarCustomers = isAdminLike
    ? allSidebarCustomers
    : allSidebarCustomers.filter((c) => callerCustomerIds.includes(c.id));

  // ADR-024 — there is no "Tous les clients" option anymore. The user must
  // always be in the context of exactly one Customer. Auto-select the first
  // one when no selection exists OR the current selection no longer matches
  // an accessible Customer (e.g. a caller's assignment was revoked).
  useEffect(() => {
    if (sidebarCustomers.length === 0) return;
    const currentlyValid = d.selectedCustomerId
      && sidebarCustomers.some((c) => c.id === d.selectedCustomerId);
    if (!currentlyValid) {
      d.setSelectedCustomerId(sidebarCustomers[0].id);
    }
  }, [sidebarCustomers, d.selectedCustomerId, d.setSelectedCustomerId]);

  // The Customer in scope: the booking link and the CRM in the "rendez-vous
  // pris" screens are read from it, as is the calendar URL below.
  const activeCustomer = sidebarCustomers.find((c) => c.id === d.selectedCustomerId) ?? null;

  // ── ⌘K / Ctrl+K global shortcut ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowGlobalSearch(prev => !prev);
        return;
      }
      // CEO-4 : `?` ouvre l'aide des raccourcis, jamais pendant une saisie.
      // Never over an open window (the call sheet, a qualification dialog).
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)
        && !document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
        e.preventDefault();
        setShowShortcuts(true);
        return;
      }
      // ⌘B replie le rail ; l'ancienne barre latérale gère le sien elle-même.
      if ((e.metaKey || e.ctrlKey) && e.key === 'b' && getUiFlags().ui_refonte) {
        e.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Determine current view from URL. Supports both nested dialer paths
  // (/ParallelDialer/<segment>) and standalone short paths that share
  // the dialer chrome (e.g. /team).
  const stripped = location.pathname.replace(/^\/(ParallelDialer\/?|)/, '');
  const currentSegment = stripped.split('/')[0] || '';
  const selectedView = ROUTE_TO_VIEW[currentSegment] || 'queue';
  // Flags frozen for this page load (lib/flags, DES-6).
  const uiFlags = getUiFlags();
  const callPageOpen = useCallPageStore((s) => s.pageOpen || !!s.qualifying || !!s.countdown || !!s.summary);
  const liveCall = useLiveCallContact();
  const showCallPage = uiFlags.call_page && selectedView === 'queue' && (callPageOpen || liveCall.hasCallOnScreen);
  // The CRM page is named after the active customer's provider (W4, B2-crm).
  const viewTitleKey = selectedView === 'crm' ? crmTitleKey(crmProviderShown(activeCustomer?.active_crm_provider)) : HEADER_BY_VIEW[selectedView]?.title;
  useDocumentTitle(viewTitleKey ? t(viewTitleKey) : t('dialer:nav.home'));

  // Navigate via React Router instead of setState.
  //
  // « Nouveau RDV » ouvrait l'agenda du client dans un onglet, et le rendez-vous
  // n'arrivait jamais dans le CRM : l'agent quittait Revcall et rien n'en
  // gardait trace. Depuis le retrait de « Rendez-vous pris » du menu, c'était
  // aussi la seule entrée restante pour un rendez-vous (Kevin, 2026-09-30).
  //
  // Les deux mènent désormais à la même fenêtre, qui enregistre le rendez-vous
  // et le pousse vers Swoft. Le lien d'agenda n'est pas perdu : la fenêtre
  // l'offre elle-même pour un client sans CRM connecté (showsBookingLink).
  const handleViewChange = (viewId, search = '') => {
    if (viewId === 'booked-meeting' || viewId === 'new-meeting') {
      setShowBookedMeeting(true);
      return;
    }
    const path = pathForView(viewId);
    navigate(search ? path + (path.includes('?') ? '&' : '?') + search.replace(/^\?/, '') : path);
  };

  // « Nouveau RDV » depuis l'écran d'accueil ouvre la même fenêtre que depuis
  // le menu. Il ouvrait un onglet sur la liste des rendez-vous, ce qui n'est
  // ni le même geste ni le même résultat.
  useEffect(() => {
    const open = () => setShowBookedMeeting(true);
    window.addEventListener('revcall:new-meeting', open);
    return () => window.removeEventListener('revcall:new-meeting', open);
  }, []);

  // ── Auth guard ─────────────────────────────────────────────────────────────
  if (!d.callerData) {
    // RequireSpace already checked the session. callerMe failing three times
    // here is a 429/5xx/network error, not a logout: offer a retry (RUI-13).
    if (d.needsLogin) return <SessionUnavailable />;
    return (
      <div className="bg-[var(--vf-bg)] min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--vf-accent-text)] animate-spin" />
      </div>
    );
  }

  // Who may enter the dialer (super admin → back-office, viewer → portal) is
  // decided once, before this layout mounts, by RequireSpace (RUI-13).

  // ── Wait for the Customer list before deciding what to render ──────────────
  // Without this, the empty-state ("Aucun client assigné") flashes for ~200ms
  // every page load while /customers is in flight — useQuery returns data=[]
  // immediately during isPending, so length===0 evaluates true prematurely.
  if (customersPending) {
    return (
      <div className="bg-[var(--vf-bg)] min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--vf-accent-text)] animate-spin" />
      </div>
    );
  }

  // ── No Customer accessible — prompt creation (admin) or assignment (caller) ─
  // ADR-024: a Customer is mandatory. If the user has zero accessible
  // Customers we can't render the dialer (every screen is Customer-scoped).
  if (sidebarCustomers.length === 0) {
    // The prompt's "create my first customer" button leads to /team, which is
    // mounted inside this same layout. Gating it too sent the admin back to the
    // prompt in a loop, so an admin on /team gets the page without the dialer
    // chrome (the sidebar needs a Customer to exist).
    // QA-W7-6: the same for Paramètres and Facturation, which an admin needs
    // to set the organisation up before its first client.
    if (isAdminLike && adminPageWithoutCustomer(location.pathname)) {
      return <NoCustomerAdminFrame />;
    }
    return <NoCustomersPrompt isAdmin={isAdminLike} onLogout={d.handleLogout} />;
  }

  return (
    <LiveTranscriptsProvider>
    <div
      id="xdial-root"
      // relative: absolutely positioned descendants without a positioned
      // ancestor escaped overflow-hidden and widened the page (QA-W4-4).
      className="relative h-screen flex overflow-hidden transition-colors"
      style={{ background: 'var(--vf-bg)', color: 'var(--vf-text-1)' }}
    >
      {/* ── Vertical sidebar (hidden on mobile, shown >= md) ──
          h-screen wrapper pour que la sidebar reste visible meme quand le
          contenu interne scrolle (le scroll est confine au Outlet, voir
          plus bas). */}
      <div className="hidden md:block h-screen">
      {/* W1 : le rail du nouveau shell derrière ui_refonte (éteint par défaut) ;
          drapeau éteint, l'ancienne barre latérale à l'identique (SR1-flag). */}
      {uiFlags.ui_refonte ? (
        <DialerRail
          selectedView={selectedView}
          onViewChange={handleViewChange}
          stats={navStats}
          listPaused={d.dialerRunState === 'paused'}
          theme={d.theme}
          cycleTheme={d.cycleTheme}
          navTabOrder={d.navTabOrder}
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          onSearchOpen={() => setShowGlobalSearch(true)}
          onTourStart={() => setShowTour(true)}
          onLiveFeedOpen={() => setShowLiveFeed(true)}
          customers={sidebarCustomers}
          selectedCustomerId={d.selectedCustomerId}
          onCustomerChange={d.setSelectedCustomerId}
        />
      ) : (
      <XDialSidebar
        selectedView={selectedView}
        onViewChange={handleViewChange}
        stats={navStats}
        listPaused={d.dialerRunState === 'paused'}
        theme={d.theme}
        cycleTheme={d.cycleTheme}
        callerName={d.callerData?.caller_name}
        onLogout={d.handleLogout}
        navTabOrder={d.navTabOrder}
        onNavTabOrderChange={d.setNavTabOrder}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        onSearchOpen={() => setShowGlobalSearch(true)}
        onTourStart={() => setShowTour(true)}
        onLiveFeedOpen={() => setShowLiveFeed(true)}
        t={d.t}
        customers={sidebarCustomers}
        selectedCustomerId={d.selectedCustomerId}
        onCustomerChange={d.setSelectedCustomerId}
      />
      )}
      </div>

      {/* ── Mobile drawer sidebar (< md) ── */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className={`p-0 ${uiFlags.ui_refonte ? 'w-[224px]' : 'w-[200px]'}`} style={{ background: 'var(--vf-surface-1)' }}>
          {/* Radix asks every dialog for a title and a description (QA-W4-8). */}
          <SheetTitle className="sr-only">{t('dialer:nav.mainMenu')}</SheetTitle>
          <SheetDescription className="sr-only">{t('dialer:nav.mobileMenuDescription')}</SheetDescription>
          {uiFlags.ui_refonte ? (
            <DialerRail
              selectedView={selectedView}
              onViewChange={(viewId) => { handleViewChange(viewId); setMobileNavOpen(false); }}
              stats={navStats}
              listPaused={d.dialerRunState === 'paused'}
              theme={d.theme}
              cycleTheme={d.cycleTheme}
              navTabOrder={d.navTabOrder}
              collapsed={false}
              onCollapsedChange={() => {}}
              collapsible={false}
              onSearchOpen={() => { setShowGlobalSearch(true); setMobileNavOpen(false); }}
              onTourStart={() => { setShowTour(true); setMobileNavOpen(false); }}
              onLiveFeedOpen={() => { setShowLiveFeed(true); setMobileNavOpen(false); }}
              customers={sidebarCustomers}
              selectedCustomerId={d.selectedCustomerId}
              onCustomerChange={d.setSelectedCustomerId}
            />
          ) : (
          <XDialSidebar
            selectedView={selectedView}
            onViewChange={(viewId) => { handleViewChange(viewId); setMobileNavOpen(false); }}
            stats={navStats}
            listPaused={d.dialerRunState === 'paused'}
            theme={d.theme}
            cycleTheme={d.cycleTheme}
            callerName={d.callerData?.caller_name}
            onLogout={d.handleLogout}
            navTabOrder={d.navTabOrder}
            onNavTabOrderChange={d.setNavTabOrder}
            collapsed={false}
            onCollapsedChange={() => {}}
            onSearchOpen={() => { setShowGlobalSearch(true); setMobileNavOpen(false); }}
            onTourStart={() => { setShowTour(true); setMobileNavOpen(false); }}
            onLiveFeedOpen={() => { setShowLiveFeed(true); setMobileNavOpen(false); }}
            t={d.t}
            customers={sidebarCustomers}
            selectedCustomerId={d.selectedCustomerId}
            onCustomerChange={d.setSelectedCustomerId}
          />
          )}
        </SheetContent>
      </Sheet>

      {/* ── Content column ── (relative: the call bar is laid out inside
          it, beside the sidebar instead of over it, QA-W4-3) */}
      <div className="relative flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Barre user : notifications + avatar + menu — TOUT en haut, dissociee
            de XDialTopBar pour que le profil ne partage plus la ligne des listes. */}
        <UserHeaderBar callerData={d.callerData} onViewChange={handleViewChange} />

        {/* Mobile hamburger row — visible < md only */}
        <div
          className="md:hidden flex items-center gap-2 h-11 px-3 border-b flex-shrink-0"
          style={{ background: 'var(--vf-bg)', borderColor: 'var(--vf-border)' }}
        >
          <button
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('dialer:nav.openMenu')}
            title={t('dialer:nav.openMenu')}
            className="h-9 w-9 flex items-center justify-center rounded-vf"
            style={{
              background: 'var(--vf-surface-2)',
              border: '1px solid var(--vf-border)',
              color: 'var(--vf-text-1)',
            }}
          >
            <Menu size={18} aria-hidden />
          </button>
          <span className="text-[14px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>
            XDial
          </span>
        </div>

        {/* W3 (ADR-0047): behind call_page, the call page takes the list's
            place during a session; the old modal and bar are not used there. */}
        {showCallPage ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            <CallPage
              customer={activeCustomer}
              onBackToList={() => { useCallPageStore.getState().close(); d.setSelectedProspect(null); }}
            />
          </div>
        ) : (
        <>
        {/* PageHeader VF unifie — rendu ici pour TOUTES les vues afin que le
            titre apparaisse a la meme hauteur partout (juste sous UserHeaderBar).
            Chaque page retire son propre PageHeader interne. */}
        {(() => {
          const meta = HEADER_BY_VIEW[selectedView];
          if (!meta) return null;
          return (
            <div
              className="flex items-center justify-between gap-4 px-5 pt-4 pb-2 flex-shrink-0"
              style={{ background: 'var(--vf-bg)' }}
            >
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold mb-0.5 truncate" style={{ color: 'var(--vf-text-1)' }}>
                  {t(viewTitleKey ?? meta.title, { defaultValue: meta.title })}
                  {/* Correction #90 — le nom de la liste active n'apparaissait
                      que dans un onglet discret. Il se lit maintenant dans le
                      titre de la vue : on sait toujours QUELLE liste on appelle. */}
                  {selectedView === 'queue' && d.selectedSource && (
                    <>
                      <span style={{ color: 'var(--vf-text-4)', fontWeight: 400, margin: '0 8px' }}>/</span>
                      <span style={{ fontWeight: 700 }}>{d.selectedSource}</span>
                    </>
                  )}
                </h1>
                {meta.subtitle && (
                  <p className="text-sm" style={{ color: 'var(--vf-text-3)' }}>
                    {t(meta.subtitle, { defaultValue: meta.subtitle })}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* 44px top bar */}
        <XDialTopBar
          sources={d.sources}
          selectedSource={d.selectedSource}
          onSourceChange={d.setSelectedSource}
          prospectsBySource={d.prospectsBySource}
          prospectQueue={d.prospectQueue}
          onViewChange={handleViewChange}
          selectedView={selectedView}
          callerData={d.callerData}
          latestCallByPhone={d.latestCallByPhone}
          t={d.t}
        />

        {/* VF: Status bar visible hors Dialer quand session active (GAP-8) */}
        <OnCallStatusBar />

        {/* Correction #3 — bandeau de temps de la Power Hour. Aucune commande
            d'appel : chrono, avancement, et bouton d'arret. */}
        <PowerHourBanner />

        {/* Toolbar: Skip + stats de liste + Script (vue queue uniquement).
            Smart Queue retire (pas de valeur ajoutee vs. tri par score du tableau).
            Correction #3 : le bouton Power Hour a ete retire d'ici. */}
        {selectedView === 'queue' && (
          <div
            // flex-wrap (QA-W4-7): at 390 px the stats wrap under the filters
            // instead of being cut.
            className="flex flex-wrap items-center gap-2 px-3 md:px-5 py-1.5 flex-shrink-0"
            style={{ background: 'var(--vf-surface-1)' }}
          >
            {d.dialerRunState === 'running' && (
              <button
                onClick={d.handleSkipBatch}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:bg-accent hover:text-foreground transition-all"
                title={t('dialer:layout.queueToolbar.skipBatchTitle')}
              >
                {t('dialer:layout.queueToolbar.skip')}
              </button>
            )}
            {/* The contacts screen portals its filter button, and the filters
                it unfolds, here: one row for filtering and reading the list. */}
            <div id="xdial-list-actions-filters" className="flex items-center gap-2 min-w-0 shrink-0" />
            <div className="flex-1" />
            {/* Stats compactes de la liste courante (Connecte / RDV / Pas de
                reponse / Nouveau) — inspire Aircall dialer. */}
            <ListCallStatsBar />
            {/* Script sits on the list row, after the list actions. */}
            <PortalSlot id="xdial-list-actions-script">
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs"
                style={showScriptPanel ? { background: 'var(--vf-accent-bg)', color: 'var(--vf-accent)' } : undefined}
                onClick={() => { setShowScriptPanel(!showScriptPanel); if (!showScriptPanel) setRightPanel('script'); }}
              >
                {t('dialer:layout.queueToolbar.script')}
              </Button>
            </PortalSlot>
          </div>
        )}

        {/* Scrollable content area + optional side panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* pt-0 : le PageHeader en Layout a deja pb-3, on evite le double
              padding qui faisait descendre le contenu trop bas. */}
          {/* pb-16: the call bar sits over the bottom 48px of this column and
              hid the last rows; the content now ends above it. */}
          <div className="min-w-0 flex-1 overflow-y-auto vf-scroll-visible px-3 pt-0 pb-16 md:px-5 md:pt-0 md:pb-16">
            {/* DES-14: one banner place, by priority (T-d1 telephony today). */}
            <DialerBannerSlot />
            <Outlet />
          </div>

          {/* Right side panel — ScriptPanel only.
              LiveTranscript a ete retire d'ici : il vit exclusivement dans la
              CallSheet modale (transcript + coach separes proprement). Avoir
              le transcript dans les deux endroits creait des doublons SSE et
              melangeait coach + transcript a droite. */}
          {(() => {
            const activeCall = d.activeCalls?.find(c => c.call_status === 'connected_to_agent');
            const showScript = showScriptPanel;
            return (
              <>
                {showScript && (
                  <div className="w-80 flex-shrink-0 border-l border-border bg-card flex flex-col overflow-hidden">
                    <ScriptPanel onClose={() => setShowScriptPanel(false)} />
                  </div>
                )}
                {/* DOUBLON SUPPRIME : on avait monte un LiveTranscript fallback
                    cache ici quand la CallSheet etait fermee, mais ca creait
                    un 2e EventSource SSE en parallele de celui de CallSheet
                    → race condition addSseClient + double facturation Whisper.
                    Le LiveTranscript de CallSheet (ligne 485, sr-only) suffit
                    pour alimenter le CoachingService tant que la CallSheet
                    est rendue. */}
              </>
            );
          })()}
        </div>
        </>
        )}

        {/* FloatingCallBar — barre unique, posee en bas de cette colonne
            (QA-W4-3 : elle couvrait le bas de la barre laterale) :
            - mode "live call" (vert) quand connectedContact !== null : avatar +
              nom + société/phone + durée + Ouvrir détails + Mute + Clavier +
              Raccrocher.
            - mode "dial control" sinon (queue view, dialer running, ou appels
              actifs) : état + parallel + caller-id + Mute + Clavier + durée
              session + Lancer/Arrêter + toggle position.
            Ex-OnCallBar mergé ici. */}
        {!showCallPage && (
          <FloatingCallBar
            selectedView={selectedView}
            onStart={uiFlags.call_page ? () => { useCallPageStore.getState().open(true); handleViewChange('queue'); } : undefined}
          />
        )}
      </div>


      {/* ── Persistent overlays (viewport-relative via fixed positioning) ── */}
      {/* ActiveCallCard retire — doublon visuel avec FloatingCallBar (meme
          critere d'affichage connected_to_agent). FloatingCallBar (mode
          live-call, design VF) est la source unique pour afficher le call
          actif. */}

      {/* 2026-05-21 — CallSheet supprimé : la modale d'appel en cours est
          désormais fusionnée dans ProspectDetailPanel (card "appel en cours"
          en haut). Voir src/components/dialer/call-panel/. */}
      <DTMFKeypad />
      <CelebrationOverlay />

      <IncomingCallToast
        incomingCall={showCallPage ? null : d.incomingCallConnection}
        prospectQueue={d.prospectQueue}
        customers={sidebarCustomers}
        onAccept={() => { void incomingSession.handleAccept(); }}
        onReject={incomingSession.handleReject}
        onOpenPreview={() => setShowIncomingPreview(true)}
      />

      {/* Un SMS reçu s'annonce comme un appel entrant, mais s'efface seul au
          bout de 4 secondes : il n'y a rien à décrocher. */}
      <IncomingSmsToast
        prospectQueue={d.prospectQueue}
        onOpenProspect={(p) => d.setSelectedProspect(p)}
      />

      <IncomingCallPreview
        open={showIncomingPreview && !!d.incomingCallConnection}
        onOpenChange={setShowIncomingPreview}
        incomingCall={d.incomingCallConnection}
        prospectQueue={d.prospectQueue}
        customers={sidebarCustomers}
        onAccept={() => { setShowIncomingPreview(false); void incomingSession.handleAccept(); }}
        onReject={() => { setShowIncomingPreview(false); incomingSession.handleReject(); }}
      />

      <AIEmailGenerator
        call={d.emailCall}
        isOpen={!!d.emailCall}
        onClose={() => d.setEmailCall(null)}
      />

      {/* ── Renseigner un rendez-vous pris (sidebar) ────────────────────── */}
      <Dialog open={showBookedMeeting} onOpenChange={setShowBookedMeeting}>
        <DialogContent className="max-w-2xl p-0 overflow-y-auto max-h-[90vh]">
          <BookedMeetingDialog
            customer={activeCustomer}
            customers={sidebarCustomers}
            onComplete={() => setShowBookedMeeting(false)}
          />
        </DialogContent>
      </Dialog>

      <ProspectDetailPanel
        prospect={showCallPage ? null : contactAffiche}
        isOpen={!showCallPage && !!contactAffiche}
        onClose={() => d.setSelectedProspect(null)}
        onCall={async (prospect) => {
          d.setSelectedProspect(null);
          // A call placed from the prospect card is a single call. Power mode
          // is session state that stays on once a run has started, and while
          // the run is 'running' the rolling refill keeps dialing the queue
          // around this call. Turning it off stops the refill; calls already
          // ringing finish normally and the run falls back to idle.
          d.setPowerMode(false);
          await d.handleCallProspect(prospect);
        }}
        onDoNotCall={(prospect) => {
          d.updateProspectQueueWithRetry(prospect.id, {
            status: 'do_not_call',
            do_not_call: true,
            do_not_call_at: new Date().toISOString(),
          }).then(async () => {
            await refreshProspects();
            d.setSelectedProspect(null);
          });
        }}
        onSnooze={async (prospect, jusqua) => {
          // #56 -- met le contact en pause jusqu'a `jusqua`. canCall() le sort
          // de la file tant que la date est dans le futur ; aucune autre action
          // n'est requise pour qu'il revienne.
          // Choosing a duration applies it at once: say so, and say when it fails
          // (the server used to refuse every pause without anyone seeing it).
          try {
            await d.updateProspectQueueWithRetry(prospect.id, { snoozed_until: jusqua });
            toast.success(t('dialer:callBar.snoozedToast', { date: formatDate(jusqua) }));
            await refreshProspects();
            // 🚨 Kevin, 2026-09-30 : la fiche reste ouverte. Mettre un contact
            // en pause est un reglage sur ce contact, pas un depart : on veut
            // voir l'etat changer sous ses yeux, et souvent enchainer sur une
            // note ou un SMS.
          } catch {
            toast.error(t('dialer:callBar.snoozeError'));
          }
        }}
        onEndSnooze={async (prospect) => {
          // #56 -- reprise anticipee : on efface la date et on relibere le
          // numero dans la session courante, comme pour une remise en file.
          // Comme la mise en pause : un toast, et la fiche reste ouverte.
          try {
            await d.updateProspectQueueWithRetry(prospect.id, { snoozed_until: null });
            if (d.clearDialedPhonesForRelance && prospect.prospect_phone) {
              d.clearDialedPhonesForRelance([prospect.prospect_phone]);
            }
            await refreshProspects();
            toast.success(t('dialer:callBar.snoozeEnd'));
          } catch {
            toast.error(t('dialer:callBar.snoozeError'));
          }
        }}
        onEnableCalls={async (prospect) => {
          // #55 -- "Enable calls". ATTENTION : onRequeue n'ecrit que
          // { status: 'active' } et laisserait do_not_call a true, or canCall()
          // teste les DEUX -- le contact serait reste bloque en silence.
          // On efface les trois champs poses par onDoNotCall.
          await d.updateProspectQueueWithRetry(prospect.id, {
            status: 'active',
            do_not_call: false,
            do_not_call_at: null,
          });
          if (d.clearDialedPhonesForRelance && prospect.prospect_phone) {
            d.clearDialedPhonesForRelance([prospect.prospect_phone]);
          }
          await refreshProspects();
          d.setSelectedProspect(null);
        }}
        onRequeue={async (prospect) => {
          // ADR-032 — 'active' au lieu de 'pending'. Réactive le prospect
          // dans la session courante via clearDialedPhonesForRelance.
          // status 'active' alone changed nothing on screen: the shown status is
          // derived from the last call. A manual "new" status newer than that
          // call makes the prospect ready to call again (dialRules).
          await d.updateProspectQueueWithRetry(prospect.id, {
            status: 'active',
            status_override: 'new',
            status_override_at: new Date().toISOString(),
          });
          if (d.clearDialedPhonesForRelance && prospect.prospect_phone) {
            d.clearDialedPhonesForRelance([prospect.prospect_phone]);
          }
          await refreshProspects();
          // Comme la mise en pause : la fiche reste ouverte. Le bouton
          // disparait de lui-meme, puisque le contact est desormais pret a
          // appeler -- c'est la preuve que l'action a bien porte.
          toast.success(t('dialer:callBar.requeue'));
        }}
      />

      {/* ── Global search / command palette (⌘K) ── */}
      {showGlobalSearch && (
        <GlobalSearchDialog
          onViewChange={(viewId, search) => {
            handleViewChange(viewId, search);
            setShowGlobalSearch(false);
          }}
          onClose={() => setShowGlobalSearch(false)}
          overdueCount={overdueFollowupsCount ?? null}
          onShortcutsHelp={() => setShowShortcuts(true)}
        />
      )}
      <ShortcutsHelpDialog open={showShortcuts} onOpenChange={setShowShortcuts} />

      {/* ── Guided tour ── */}
      {showTour && <TourGuide onClose={() => setShowTour(false)} />}

      {/* ── Live Feed panel (📡) ── */}
      <LiveFeedPanel isOpen={showLiveFeed} onClose={() => setShowLiveFeed(false)} />

      {/* ── Telephony blocker (micro bloque / Device en erreur) ──
          Modale plein ecran non-dismissible : sans micro autorise, les appels
          sortants se coupent a 1s cote Twilio sans aucune trace visuelle. */}
      <TelephonyBlocker />
    </div>
    </LiveTranscriptsProvider>
  );
}
